import { useState, useEffect } from 'react';
import ContentProtection from '../../components/ContentProtection';

export default function HomePage() {
  const [activeSection, setActiveSection] = useState('inicio');
  const [showLogoModal, setShowLogoModal] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});

  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

    const schemas = [
      {
        '@context': 'https://schema.org',
        '@type': 'LocalBusiness',
        '@id': `${siteUrl}/#business`,
        name: 'Aula K9 Arete Can',
        description: 'Más de 40 años de experiencia en adiestramiento, educación y evaluación del comportamiento canino en Navarra. Formación colaborativa, consulta online y evaluaciones profesionales.',
        url: siteUrl,
        logo: 'https://static.readdy.ai/image/1641cce445e23a33732a9af05560332a/3b12e741c87f15e57088a800f3467174.jpeg',
        image: 'https://static.readdy.ai/image/1641cce445e23a33732a9af05560332a/3b12e741c87f15e57088a800f3467174.jpeg',
        email: 'aretecan@gmail.com',
        telephone: '+34722131257',
        address: {
          '@type': 'PostalAddress',
          addressRegion: 'Navarra',
          addressCountry: 'ES'
        },
        areaServed: {
          '@type': 'State',
          name: 'Navarra'
        },
        openingHoursSpecification: [
          {
            '@type': 'OpeningHoursSpecification',
            dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
            opens: '10:00',
            closes: '14:00'
          },
          {
            '@type': 'OpeningHoursSpecification',
            dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
            opens: '16:00',
            closes: '20:00'
          }
        ],
        sameAs: [
          'https://www.facebook.com/Aulak9AreteCan',
          'https://twitter.com/aulak9yaretecan',
          'https://instagram.com/asociacionaulak9aretecan'
        ]
      },
      {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        '@id': `${siteUrl}/#website`,
        url: siteUrl,
        name: 'Aula K9 Arete Can',
        description: 'Adiestramiento y Educación Canina Profesional en Navarra',
        inLanguage: 'es-ES'
      },
      {
        '@context': 'https://schema.org',
        '@type': 'Service',
        '@id': `${siteUrl}/#service-evaluacion`,
        name: 'Evaluación Profesional Canina',
        description: 'Evaluación del comportamiento canino, análisis del entorno y pautas claras para mejorar la convivencia con tu perro.',
        provider: { '@id': `${siteUrl}/#business` },
        areaServed: { '@type': 'State', name: 'Navarra' },
        serviceType: 'Adiestramiento Canino'
      },
      {
        '@context': 'https://schema.org',
        '@type': 'Service',
        '@id': `${siteUrl}/#service-online`,
        name: 'Consulta Online de Adiestramiento Canino',
        description: 'Sesión virtual de 30 minutos por 20€ para resolver problemas de comportamiento canino.',
        provider: { '@id': `${siteUrl}/#business` },
        offers: {
          '@type': 'Offer',
          price: '20',
          priceCurrency: 'EUR'
        },
        serviceType: 'Consulta Online Canina'
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: '¿Cuánto tiempo lleva resolver un problema de comportamiento?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Cada caso es único. Depende del tipo de problema, la edad del perro, el entorno y la dedicación del propietario. En la evaluación inicial te daremos una estimación realista.'
            }
          },
          {
            '@type': 'Question',
            name: '¿Trabajáis con todos los tipos de perros?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Sí, trabajamos con perros de todas las razas, edades y tamaños. Nuestro enfoque se adapta a las características individuales de cada perro y su familia.'
            }
          },
          {
            '@type': 'Question',
            name: '¿Atendéis casos de agresividad?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Sí, pero requieren consulta previa para valoración específica. La seguridad es prioritaria y cada caso de agresividad necesita un análisis profesional detallado.'
            }
          },
          {
            '@type': 'Question',
            name: '¿Realizáis el trabajo a domicilio?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Sí, las evaluaciones se realizan preferentemente en el entorno habitual del perro para obtener una visión completa del problema y la convivencia familiar.'
            }
          }
        ]
      }
    ];

    const existingScript = document.getElementById('schema-org-jsonld');
    if (existingScript) existingScript.remove();

    const script = document.createElement('script');
    script.id = 'schema-org-jsonld';
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(schemas);
    document.head.appendChild(script);

    return () => {
      const s = document.getElementById('schema-org-jsonld');
      if (s) s.remove();
    };
  }, []);

  const scrollToSection = (sectionId: string) => {
    setActiveSection(sectionId);
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const toggleSection = (sectionId: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-teal-50">
      <ContentProtection />
      
      {/* Header - Responsive para móvil y escritorio */}
      <header className="fixed top-0 w-full bg-white/95 backdrop-blur-md shadow-md z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div 
                className="w-12 h-12 sm:w-14 sm:h-14 rounded-full overflow-hidden flex items-center justify-center cursor-pointer hover:ring-4 hover:ring-teal-200 transition-all"
                onClick={() => setShowLogoModal(true)}
              >
                <img 
                  src="https://static.readdy.ai/image/1641cce445e23a33732a9af05560332a/3b12e741c87f15e57088a800f3467174.jpeg"
                  alt="Aula K9 Logo"
                  className="w-full h-full object-cover"
                />
              </div>
              <div>
                <h1 className="text-lg sm:text-xl font-bold text-teal-800">
                  <span style={{ fontFamily: 'Georgia, serif', fontWeight: 'bold', fontStyle: 'italic' }}>Aula K</span><span style={{ fontFamily: 'Arial, sans-serif', fontWeight: 'bold', fontStyle: 'italic' }}>9</span><span style={{ fontFamily: 'Georgia, serif', fontWeight: 'bold', fontStyle: 'italic' }}> Arete Can</span>
                </h1>
                <p className="text-[10px] sm:text-xs text-teal-700 leading-tight hidden sm:block">Formación Colaborativa, Educación, Adiestramiento,<br/>Evaluación y Bienestar Canino</p>
                <p className="text-[9px] text-teal-700 leading-tight sm:hidden">Formación y Bienestar Canino</p>
              </div>
            </div>
            {/* Menú hamburguesa para móvil */}
            <button 
              onClick={() => toggleSection('menu')}
              className="lg:hidden w-10 h-10 flex items-center justify-center"
            >
              <i className={`${expandedSections['menu'] ? 'ri-close-line' : 'ri-menu-line'} text-2xl text-teal-800`}></i>
            </button>
            {/* Navegación desktop */}
            <nav className="hidden lg:flex space-x-6">
              {[
                { id: 'inicio', label: 'Inicio' },
                { id: 'quien-ayudamos', label: '¿A quién ayudamos?' },
                { id: 'servicios', label: 'Servicios' },
                { id: 'consulta-online', label: 'Consulta Online' },
                { id: 'profesional', label: 'Profesional' },
                { id: 'contacto', label: 'Contacto' }
              ].map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className={`text-sm font-medium transition-all duration-300 hover:scale-105 hover:bg-orange-100 px-3 py-2 rounded-lg ${
                    activeSection === item.id ? 'text-orange-500 bg-orange-50' : 'text-teal-700 hover:text-orange-500'
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </nav>
          </div>
          {/* Menú móvil desplegable */}
          {expandedSections['menu'] && (
            <nav className="lg:hidden mt-3 pb-2 space-y-2 border-t border-teal-100 pt-3">
              {[
                { id: 'inicio', label: 'Inicio' },
                { id: 'quien-ayudamos', label: '¿A quién ayudamos?' },
                { id: 'servicios', label: 'Servicios' },
                { id: 'consulta-online', label: 'Consulta Online' },
                { id: 'profesional', label: 'Profesional' },
                { id: 'contacto', label: 'Contacto' }
              ].map((item) => (
                <button
                  key={item.id}
                  onClick={() => {
                    scrollToSection(item.id);
                    toggleSection('menu');
                  }}
                  className={`block w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-all duration-300 hover:scale-105 ${
                    activeSection === item.id ? 'bg-orange-500 text-white' : 'text-teal-700 hover:bg-orange-100'
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </nav>
          )}
        </div>
      </header>

      {/* Logo Modal */}
      {showLogoModal && (
        <div 
          className="fixed inset-0 bg-black/80 z-[100] flex items-center justify-center p-4"
          onClick={() => setShowLogoModal(false)}
        >
          <div className="relative max-w-2xl w-full">
            <button
              onClick={() => setShowLogoModal(false)}
              className="absolute -top-12 right-0 text-white hover:text-teal-300 transition-colors"
            >
              <i className="ri-close-line text-3xl"></i>
            </button>
            <img 
              src="https://static.readdy.ai/image/1641cce445e23a33732a9af05560332a/3b12e741c87f15e57088a800f3467174.jpeg"
              alt="Aula K9 Logo"
              className="w-full h-auto rounded-2xl shadow-2xl"
            />
          </div>
        </div>
      )}

      {/* Contenedor principal con ancho máximo para escritorio */}
      <main className="max-w-7xl mx-auto">
        {/* Hero Section - Responsive */}
        <section id="inicio" className="pt-20 sm:pt-24 pb-8 px-4 sm:px-6 lg:px-8">
          <div className="space-y-6">
            <div className="space-y-3 text-center">
              <p className="text-orange-500 font-semibold text-xs sm:text-sm tracking-wide uppercase">"Tú también puedes. Convierte el apego/vínculo con tu perro en algo amgico y único"</p>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-teal-800 leading-tight">
                <span style={{ fontFamily: 'Georgia, serif', fontWeight: 'bold', fontStyle: 'italic' }}>Aula K</span><span style={{ fontFamily: 'Arial, sans-serif', fontWeight: 'bold', fontStyle: 'italic' }}>9</span><span style={{ fontFamily: 'Georgia, serif', fontWeight: 'bold', fontStyle: 'italic' }}> Arete Can</span>
              </h1>
              <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-orange-500 max-w-4xl mx-auto">Estamos comprometidos con la Excelencia en la Formación Colaborativa • Educación • Formacion y Adiestramiento Canino.<br className="hidden sm:block"/>Evaluación: del comportamiento y las conductas caninas. Evaluaciones de agresiones a: personas animales o bienes.</h2>
            </div>
            
            {/* Dos imágenes lado a lado - Responsive */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 lg:gap-6">
              <div className="relative rounded-2xl overflow-hidden shadow-xl hover:scale-105 transition-all duration-300 transform bg-gray-100">
                <img
                  src="https://static.readdy.ai/image/1641cce445e23a33732a9af05560332a/54477988f0f7c59c5bc2d7a7ef95eb7f.png"
                  alt="Instructor profesional"
                  className="w-full h-64 sm:h-72 lg:h-80 object-contain"
                />
              </div>
              <div className="relative rounded-2xl overflow-hidden shadow-xl hover:scale-105 transition-all duration-300 transform bg-gray-100">
                <img
                  src="https://static.readdy.ai/image/1641cce445e23a33732a9af05560332a/851b1c450b45cf7325be85924c8e03cd.jpeg"
                  alt="Entrenamiento profesional"
                  className="w-full h-64 sm:h-72 lg:h-80 object-contain"
                />
              </div>
            </div>
            
            {/* Hero Principal - Responsive */}
            <div className="bg-gradient-to-br from-teal-600 to-blue-600 rounded-2xl p-6 sm:p-8 lg:p-10 shadow-xl text-white">
              <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold mb-3 text-center">Información y Formación responsable y profesional para una vida tranquila con tu perro</h2>
              <p className="text-base sm:text-lg leading-relaxed text-center mb-6 max-w-3xl mx-auto">
                Te acompaño paso a paso para que entiendas a tu perro, evitar problemas de comportamiento y disfrutes de una convivencia equilibrada, respetuosa y feliz.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center max-w-xl mx-auto">
                <button
                  onClick={() => scrollToSection('servicios')}
                  className="flex-1 bg-white hover:bg-orange-50 hover:scale-105 text-teal-700 px-6 py-3 rounded-full font-semibold transition-all duration-300 shadow-lg transform"
                >
                  Quiero formarme como tutor responsable
                </button>
                <button
                  onClick={() => scrollToSection('como-funciona')}
                  className="flex-1 bg-orange-500 hover:bg-orange-600 hover:scale-105 text-white px-6 py-3 rounded-full font-semibold transition-all duration-300 shadow-lg transform"
                >
                  Ver cómo funciona
                </button>
              </div>
              <p className="text-sm text-center italic text-white/90 mt-4">
                "No se trata de tener un perro perfecto, sino de construir una relación sana, consciente y responsable."
              </p>
            </div>
            
            {/* Badge de urgencia y confianza */}
            <div className="bg-orange-50 border-2 border-orange-300 rounded-xl p-4 sm:p-5">
              <div className="flex items-center justify-center space-x-2 mb-2">
                <i className="ri-time-line text-orange-600 text-xl"></i>
                <p className="text-orange-800 font-bold text-sm sm:text-base">⚡ Plazas limitadas disponibles</p>
              </div>
              <p className="text-gray-700 text-xs sm:text-sm text-center">
                Más de <strong>40 años de experiencia</strong> ayudando a familias a mejorar la convivencia con sus perros
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="bg-white rounded-xl p-4 sm:p-5 shadow-md">
                <p className="text-gray-700 text-sm sm:text-base leading-relaxed text-center">
                  En este proceso de aprendizaje, impartimos una <strong>formación</strong> atendiendo a unos <strong>altos estándares de calidad</strong>. Estamos comprometidos con la excelencia en la formación.. No todo el mundo puede ejercer profesionalmente, nosotros si.
                </p>
              </div>
              <div className="bg-teal-50 border-l-4 border-teal-600 p-4 sm:p-5 rounded-r-lg flex items-center">
                <p className="text-teal-800 text-sm sm:text-base font-medium">
                  Servicio de Evaluación Profesional Canina con un lenguaje sencillo, ordenado, claro, profesional y respetuoso.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Prompt motivacional 1 */}
        <section className="py-6 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-orange-100 to-orange-200">
          <div className="max-w-4xl mx-auto text-center">
            <div className="bg-white rounded-2xl p-5 sm:p-6 lg:p-8 shadow-lg border-2 border-orange-300">
              <div className="mb-4">
                <p className="text-gray-700 text-sm sm:text-base leading-relaxed italic mb-2">
                  "Cada día tenemos algo que aprender, algo que soltar y muchas razones para agradecer.<br/>
                  La raza predispone, el entorno condiciona y el individuo determina."
                </p>
                <p className="text-orange-600 text-xs sm:text-sm font-semibold">– Aula K9 Don Bandi</p>
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-orange-600 mb-2">¿Te interesa mejorar la relación con tu perro?</h3>
              <p className="text-gray-700 text-sm sm:text-base mb-3">Descubre cómo podemos ayudarte paso a paso</p>
              <button
                onClick={() => scrollToSection('contacto')}
                className="bg-orange-500 hover:bg-orange-600 hover:scale-105 text-white px-6 py-2 sm:py-3 rounded-full font-semibold transition-all duration-300 text-sm sm:text-base shadow-md transform"
              >
                ¡Contacta con nosotros!
              </button>
            </div>
          </div>
        </section>

        {/* Identidad y Propósito */}
        <section className="py-6 px-4 sm:px-6 lg:px-8 bg-white/70">
          <div className="max-w-4xl mx-auto">
            {/* Imagen fuera del desplegable */}
            <div className="relative rounded-xl overflow-hidden shadow-lg mb-4 hover:scale-105 transition-all duration-300 transform bg-gray-100">
              <img
                src="https://public.readdy.ai/ai/img_res/4550eed07bb8ae49366066ba08c1ded0.jpg"
                alt="Identidad y propósito profesional"
                className="w-full h-56 sm:h-64 lg:h-80 object-contain"
              />
            </div>
            
            <button
              onClick={() => toggleSection('identidad')}
              className="w-full flex items-center justify-between bg-white rounded-xl p-4 shadow-md mb-3 hover:bg-orange-50 hover:scale-105 transition-all duration-300 transform"
            >
              <h2 className="text-xl sm:text-2xl font-bold text-teal-800">Identidad y Propósito</h2>
              <i className={`${expandedSections['identidad'] ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-2xl text-teal-600`}></i>
            </button>
            
            {expandedSections['identidad'] && (
              <div className="space-y-4 animate-fadeIn">
                <div className="bg-white rounded-xl p-4 sm:p-5 shadow-md">
                  <p className="text-gray-700 text-sm sm:text-base leading-relaxed">
                    <strong>AULA K9</strong> es un espacio profesional dedicado a ayudar a propietarios de perros a comprender, evaluar y mejorar el comportamiento de sus animales.
                  </p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="bg-white rounded-xl p-4 shadow-md border-l-4 border-red-500">
                    <div className="flex items-start space-x-3">
                      <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <i className="ri-close-line text-red-600 text-lg"></i>
                      </div>
                      <p className="text-gray-700 text-sm sm:text-base font-medium">No trabajamos con opiniones ni soluciones improvisadas.</p>
                    </div>
                  </div>
                  <div className="bg-white rounded-xl p-4 shadow-md border-l-4 border-teal-500">
                    <div className="flex items-start space-x-3">
                      <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <i className="ri-check-line text-teal-600 text-lg"></i>
                      </div>
                      <p className="text-gray-700 text-sm sm:text-base font-medium">Trabajamos con criterio profesional, formación oficial y más de 40 años de práctica real.</p>
                    </div>
                  </div>
                </div>
                <div className="bg-gradient-to-r from-teal-50 to-blue-50 rounded-xl p-4 border border-teal-100">
                  <p className="text-teal-800 text-sm sm:text-base font-semibold text-center">
                    Nuestro objetivo es mejorar la convivencia, el bienestar del perro y la tranquilidad de las familias.
                  </p>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Prompt motivacional 2 */}
        <section className="py-6 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-teal-100 to-blue-100">
          <div className="max-w-4xl mx-auto text-center">
            <div className="bg-white rounded-2xl p-5 sm:p-6 lg:p-8 shadow-lg border-2 border-teal-300">
              <div className="mb-4">
                <p className="text-gray-700 text-sm sm:text-base leading-relaxed italic mb-2">
                  "La raza influye, el entorno impacta, pero es el individuo quien determina la historia.<br/>
                  Esto vale para perros y para personas."
                </p>
                <p className="text-teal-600 text-xs sm:text-sm font-semibold">– Aula K9 Don Bandi</p>
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-teal-600 mb-2">¿Quieres ayudar a tu perro?</h3>
              <p className="text-gray-700 text-sm sm:text-base mb-3">Motivación continua para lograr una convivencia perfecta</p>
              <div className="flex flex-col sm:flex-row gap-2 justify-center">
                <button
                  onClick={() => scrollToSection('servicios')}
                  className="bg-teal-500 hover:bg-teal-600 hover:scale-105 text-white px-4 py-2 rounded-full font-semibold transition-all duration-300 text-sm shadow-md transform"
                >
                  Ver Servicios
                </button>
                <button
                  onClick={() => scrollToSection('contacto')}
                  className="bg-orange-500 hover:bg-orange-600 hover:scale-105 text-white px-4 py-2 rounded-full font-semibold transition-all duration-300 text-sm shadow-md transform"
                >
                  Contáctanos
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* ¿A quién ayudamos? */}
        <section id="quien-ayudamos" className="py-6 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            {/* Imagen fuera del desplegable */}
            <div className="relative rounded-xl overflow-hidden shadow-lg mb-4 hover:scale-105 transition-all duration-300 transform bg-gray-100">
              <img
                src="https://readdy.ai/api/search-image?query=Dog%20owner%20with%20their%20pet%20seeking%20professional%20help%2C%20concerned%20owner%20with%20dog%2C%20professional%20canine%20consultation%2C%20realistic%20photography%20indoor%20or%20outdoor%20setting%20natural%20lighting%20high%20quality%20detailed%20image%20the%20main%20subject%20should%20take%20up%2070%25%20of%20the%20frame%20centered%20composition&width=800&height=450&seq=quien-ayudamos-img&orientation=landscape"
                alt="A quién ayudamos"
                className="w-full h-56 sm:h-64 lg:h-80 object-contain"
              />
            </div>
            
            <button
              onClick={() => toggleSection('quien-ayudamos')}
              className="w-full flex items-center justify-between bg-white rounded-xl p-4 shadow-md mb-3 hover:bg-orange-50 hover:scale-105 transition-all duration-300 transform"
            >
              <h2 className="text-xl sm:text-2xl font-bold text-teal-800">¿A quién ayudamos?</h2>
              <i className={`${expandedSections['quien-ayudamos'] ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-2xl text-teal-600`}></i>
            </button>
            
            {expandedSections['quien-ayudamos'] && (
              <div className="space-y-4 animate-fadeIn">
                <div className="bg-white rounded-xl p-4 sm:p-5 shadow-md">
                  <h3 className="text-base sm:text-lg font-bold text-teal-800 mb-3">A propietarios que:</h3>
                  <div className="space-y-3">
                    {[
                      'Conviven con problemas de comportamiento que afectan a su día a día.',
                      'Han probado consejos genéricos sin resultados.',
                      'Buscan ayuda profesional, seria y respetuosa.',
                      'Quieren entender a su perro y actuar correctamente.'
                    ].map((item, index) => (
                      <div key={index} className="flex items-start space-x-2">
                        <div className="w-5 h-5 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <i className="ri-check-line text-teal-600 text-xs"></i>
                        </div>
                        <p className="text-gray-700 text-sm sm:text-base leading-relaxed">{item}</p>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="bg-orange-50 border-l-4 border-orange-500 p-4 rounded-r-lg">
                  <p className="text-orange-800 text-sm sm:text-base font-semibold text-center">
                    👉 El problema no es tu perro. Lo que necesita es la ayuda adecuada.
                  </p>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Prompt motivacional 3 */}
        <section className="py-6 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-blue-100 to-purple-100">
          <div className="max-w-4xl mx-auto text-center">
            <div className="bg-white rounded-2xl p-5 sm:p-6 lg:p-8 shadow-lg border-2 border-blue-300">
              <div className="mb-4">
                <p className="text-gray-700 text-sm sm:text-base leading-relaxed italic mb-2">
                  "No es solo la raza ni solo el entorno.<br/>
                  Es lo que hacemos cada día lo que determina la convivencia con nuestro perro."
                </p>
                <p className="text-blue-600 text-xs sm:text-sm font-semibold">– Aula K9 Don Bandi</p>
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-blue-600 mb-2">¿Te gustaría aprender con nosotros?</h3>
              <p className="text-gray-700 text-sm sm:text-base mb-3">Formación colaborativa que transforma vidas</p>
              <button
                onClick={() => scrollToSection('contacto')}
                className="bg-blue-500 hover:bg-blue-600 hover:scale-105 text-white px-6 py-2 rounded-full font-semibold transition-all duration-300 text-sm shadow-md transform"
              >
                ¡Empezamos juntos!
              </button>
            </div>
          </div>
        </section>

        {/* Problemas de Comportamiento */}
        <section className="py-6 px-4 sm:px-6 lg:px-8 bg-white/70">
          <div className="max-w-4xl mx-auto">
            {/* Imagen fuera del desplegable */}
            <div className="relative rounded-xl overflow-hidden shadow-lg mb-4 hover:scale-105 transition-all duration-300 transform bg-gray-100">
              <img
                src="https://readdy.ai/api/search-image?query=Common%20dog%20behavior%20problems%2C%20dog%20showing%20various%20behavioral%20issues%2C%20professional%20dog%20training%20assessment%2C%20realistic%20photography%20outdoor%20or%20indoor%20setting%20natural%20lighting%20high%20quality%20detailed%20image%20the%20main%20subject%20should%20take%20up%2070%25%20of%20the%20frame%20centered%20composition&width=800&height=450&seq=problemas-comportamiento-img&orientation=landscape"
                alt="Problemas de comportamiento canino"
                className="w-full h-56 sm:h-64 lg:h-80 object-contain"
              />
            </div>
            
            <button
              onClick={() => toggleSection('problemas')}
              className="w-full flex items-center justify-between bg-white rounded-xl p-4 shadow-md mb-3 hover:bg-orange-50 hover:scale-105 transition-all duration-300 transform"
            >
              <h2 className="text-xl sm:text-2xl font-bold text-teal-800">Problemas más habituales</h2>
              <i className={`${expandedSections['problemas'] ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-2xl text-teal-600`}></i>
            </button>
            
            {expandedSections['problemas'] && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 animate-fadeIn">
                {[
                  { icon: 'ri-walk-line', title: 'Tirones de correa', desc: 'Falta de control en el paseo' },
                  { icon: 'ri-emotion-unhappy-line', title: 'Ansiedad por separación', desc: 'Ladridos, destrucción, angustia' },
                  { icon: 'ri-shield-cross-line', title: 'Miedos e inseguridad', desc: 'Estrés y comportamiento temeroso' },
                  { icon: 'ri-home-gear-line', title: 'Conductas destructivas', desc: 'Destrucción en casa' },
                  { icon: 'ri-alert-line', title: 'Agresividad', desc: 'Hacia personas, perros u objetos' },
                  { icon: 'ri-more-line', title: 'Otros problemas', desc: 'Cada caso es único' }
                ].map((problema, index) => (
                  <div key={index} className="bg-white rounded-xl p-4 shadow-md border border-teal-100 hover:bg-orange-50 hover:scale-105 transition-all duration-300 transform">
                    <div className="flex items-start space-x-3">
                      <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <i className={`${problema.icon} text-teal-600 text-lg`}></i>
                      </div>
                      <div>
                        <h3 className="text-sm font-bold text-teal-800 mb-1">{problema.title}</h3>
                        <p className="text-gray-600 text-xs">{problema.desc}</p>
                      </div>
                    </div>
                  </div>
                ))}
                <div className="bg-teal-50 rounded-xl p-4 text-center sm:col-span-2 lg:col-span-3">
                  <p className="text-teal-800 font-medium text-sm sm:text-base">
                    Cada caso es único. <strong>No existen soluciones universales.</strong>
                  </p>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Cómo Funciona */}
        <section id="como-funciona" className="py-6 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            {/* Imagen fuera del desplegable */}
            <div className="relative rounded-xl overflow-hidden shadow-lg mb-4 hover:scale-105 transition-all duration-300 transform bg-gray-100">
              <img
                src="https://readdy.ai/api/search-image?query=Professional%20dog%20training%20process%20step%20by%20step%2C%20trainer%20working%20with%20dog%20owner%2C%20professional%20consultation%20and%20evaluation%2C%20realistic%20photography%20outdoor%20or%20indoor%20setting%20natural%20lighting%20high%20quality%20detailed%20image%20the%20main%20subject%20should%20take%20up%2070%25%20of%20the%20frame%20centered%20composition&width=800&height=450&seq=como-funciona-process-img&orientation=landscape"
                alt="Proceso de trabajo profesional"
                className="w-full h-56 sm:h-64 lg:h-80 object-contain"
              />
            </div>
            
            <button
              onClick={() => toggleSection('como-funciona')}
              className="w-full flex items-center justify-between bg-white rounded-xl p-4 shadow-md mb-3 hover:bg-orange-50 hover:scale-105 transition-all duration-300 transform"
            >
              <h2 className="text-xl sm:text-2xl font-bold text-teal-800">Cómo Funciona</h2>
              <i className={`${expandedSections['como-funciona'] ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-2xl text-teal-600`}></i>
            </button>
            
            {expandedSections['como-funciona'] && (
              <div className="space-y-3 animate-fadeIn">
                <p className="text-gray-600 text-sm sm:text-base text-center mb-3">Nuestro trabajo se basa en:</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {[
                    { num: '1', title: 'Exposición del problema', desc: 'El propietario nos expone el problema real del perro.' },
                    { num: '2', title: 'Evaluación completa', desc: 'Evaluamos el comportamiento, el entorno y la convivencia.' },
                    { num: '3', title: 'Pautas claras', desc: 'Ofrecemos pautas claras o derivamos al profesional más adecuado.' },
                    { num: '4', title: 'Acompañamiento', desc: 'Acompañamos el proceso cuando es necesario.' }
                  ].map((paso, index) => (
                    <div key={index} className="bg-white rounded-xl p-4 shadow-md border border-teal-100 hover:bg-orange-50 hover:scale-105 transition-all duration-300 transform">
                      <div className="flex items-start space-x-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-white text-lg font-bold">{paso.num}</span>
                        </div>
                        <div>
                          <h3 className="text-sm font-bold text-teal-800 mb-1">{paso.title}</h3>
                          <p className="text-gray-600 text-xs">{paso.desc}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="text-center mt-4">
                  <p className="text-teal-700 text-sm sm:text-base font-medium">
                    Siempre desde la calma, el respeto y el bienestar animal.
                  </p>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Prompt motivacional 4 - Compartir */}
        <section className="py-6 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-green-100 to-teal-100">
          <div className="max-w-4xl mx-auto text-center">
            <div className="bg-white rounded-2xl p-5 sm:p-6 shadow-lg border-2 border-green-300">
              <h3 className="text-lg sm:text-xl font-bold text-green-600 mb-2">¿Conoces a alguien que le pueda interesar?</h3>
              <p className="text-gray-700 text-sm sm:text-base mb-3">¡Compártelo con tus amigos y ayuda a más familias!</p>
              <div className="flex flex-col sm:flex-row justify-center gap-2">
                <button
                  onClick={() => {
                    if (navigator.share) {
                      navigator.share({
                        title: 'Aula K9 Arete Can - Educación y Bienestar Canino',
                        text: 'Descubre la mejor formación profesional en educación canina',
                        url: window.location.href,
                      });
                    } else {
                      navigator.clipboard.writeText(window.location.href);
                      alert('¡Enlace copiado! Compártelo donde quieras');
                    }
                  }}
                  className="bg-green-500 hover:bg-green-600 hover:scale-105 text-white px-4 py-2 rounded-full font-semibold transition-all duration-300 text-sm shadow-md transform flex items-center justify-center"
                >
                  <i className="ri-share-line mr-1"></i>
                  Compartir
                </button>
                <button
                  onClick={() => scrollToSection('contacto')}
                  className="bg-orange-500 hover:bg-orange-600 hover:scale-105 text-white px-4 py-2 rounded-full font-semibold transition-all duration-300 text-sm shadow-md transform"
                >
                  ¡Contáctanos ya!
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Servicios - Evaluación Profesional */}
        <section id="servicios" className="py-6 px-4 sm:px-6 lg:px-8 bg-white/70">
          <div className="max-w-5xl mx-auto">
            {/* Badge de urgencia */}
            <div className="bg-red-50 border-2 border-red-300 rounded-xl p-3 mb-4 text-center">
              <p className="text-red-700 font-bold text-sm sm:text-base flex items-center justify-center">
                <i className="ri-fire-line text-red-600 mr-2"></i>
                ⚡ Oferta especial: Reserva tu evaluación esta semana
              </p>
            </div>

            <div className="text-center mb-6">
              <h2 className="text-2xl sm:text-3xl font-bold text-teal-800 mb-2">Evaluación Profesional Canina</h2>
              <p className="text-gray-700 text-sm sm:text-base mb-3 max-w-2xl mx-auto">
                Cada perro es un individuo, por eso la evaluación es la base de cualquier intervención responsable.
              </p>
              {/* Elementos de confianza */}
              <div className="flex flex-wrap justify-center gap-2 mb-4">
                <span className="bg-teal-100 text-teal-800 px-3 py-1 rounded-full text-xs sm:text-sm font-semibold">
                  ✓ +40 años experiencia
                </span>
                <span className="bg-orange-100 text-orange-800 px-3 py-1 rounded-full text-xs sm:text-sm font-semibold">
                  ✓ Acreditación oficial
                </span>
                <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-xs sm:text-sm font-semibold">
                  ✓ Resultados garantizados
                </span>
              </div>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Opción 1 */}
              <div className="bg-white rounded-2xl p-5 sm:p-6 shadow-lg border-2 border-teal-200 hover:bg-orange-50 hover:scale-[1.02] transition-all duration-300 transform relative">
                <div className="flex items-center space-x-2 mb-4">
                  <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center">
                    <span className="text-teal-600 text-base font-bold">1</span>
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold text-teal-800">Evaluación y Plan de Pautas</h3>
                </div>
                
                <ul className="space-y-2 mb-4">
                  {[
                    'Evaluación inicial del comportamiento del perro',
                    'Análisis del entorno y la convivencia',
                    'Pautas claras y detalladas para trabajar de forma autónoma',
                    'Informe profesional por escrito'
                  ].map((item, index) => (
                    <li key={index} className="flex items-start space-x-2">
                      <i className="ri-checkbox-circle-fill text-teal-500 text-base flex-shrink-0 mt-0.5"></i>
                      <span className="text-gray-700 text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
                
                <div className="bg-teal-50 rounded-lg p-3 mb-4">
                  <p className="text-teal-800 text-xs sm:text-sm">
                    👉 <strong>Ideal para</strong> propietarios que quieren comprender el problema y empezar a mejorar la situación de inmediato.
                  </p>
                </div>

                {/* Testimonios integrados */}
                <div className="bg-gradient-to-r from-blue-50 to-teal-50 rounded-lg p-3 mb-4 border-l-4 border-blue-400">
                  <p className="text-gray-700 text-xs italic mb-1">
                    "En solo una sesión entendí por qué mi perro actuaba así. Las pautas fueron claras y funcionaron."
                  </p>
                  <p className="text-blue-600 text-xs font-semibold">— María G., Pamplona</p>
                </div>

                <button
                  onClick={() => scrollToSection('contacto')}
                  className="w-full bg-orange-500 hover:bg-orange-600 hover:scale-105 text-white px-5 py-3 rounded-full font-semibold transition-all duration-300 shadow-md text-sm transform"
                >
                  🚀 Solicitar Evaluación Ahora
                </button>
                
                <p className="text-center text-xs text-gray-500 mt-2">
                  ⏰ Respuesta en menos de 24 horas
                </p>
              </div>

              {/* Opción 2 */}
              <div className="bg-white rounded-2xl p-5 sm:p-6 shadow-lg border-2 border-orange-200 hover:bg-orange-50 hover:scale-[1.02] transition-all duration-300 transform">
                <div className="flex items-center space-x-2 mb-4">
                  <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
                    <span className="text-orange-600 text-base font-bold">2</span>
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold text-teal-800">Evaluación, Pautas y Seguimiento</h3>
                  <span className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-2 py-1 rounded-full text-xs font-bold ml-auto">
                    PREMIUM
                  </span>
                </div>
                
                <ul className="space-y-2 mb-4">
                  {[
                    'Evaluación completa y personalizada',
                    'Plan de trabajo específico y adaptado',
                    'Una semana de seguimiento profesional',
                    'Visita adicional si fuese necesaria',
                    'Soporte por WhatsApp durante el proceso'
                  ].map((item, index) => (
                    <li key={index} className="flex items-start space-x-2">
                      <i className="ri-checkbox-circle-fill text-orange-500 text-base flex-shrink-0 mt-0.5"></i>
                      <span className="text-gray-700 text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
                
                <div className="bg-orange-50 rounded-lg p-3 mb-4">
                  <p className="text-orange-800 text-xs sm:text-sm">
                    👉 <strong>Recomendada para</strong> casos complejos o cuando se necesita acompañamiento profesional continuo.
                  </p>
                </div>

                {/* Testimonios integrados */}
                <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg p-3 mb-4 border-l-4 border-purple-400">
                  <p className="text-gray-700 text-xs italic mb-1">
                    "El seguimiento fue clave. Tener apoyo durante todo el proceso marcó la diferencia. Mi perro cambió completamente."
                  </p>
                  <p className="text-purple-600 text-xs font-semibold">— Carlos R., Tudela</p>
                </div>

                <button
                  onClick={() => scrollToSection('contacto')}
                  className="w-full bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 hover:scale-105 text-white px-5 py-3 rounded-full font-semibold transition-all duration-300 shadow-md text-sm transform"
                >
                  💎 Solicitar Evaluación Premium
                </button>
                
                <p className="text-center text-xs text-gray-500 mt-2">
                  ⏰ Respuesta en menos de 24 horas
                </p>
              </div>
            </div>

            {/* Sección de testimonios ampliada */}
            <div className="mt-8 bg-gradient-to-br from-blue-50 to-teal-50 rounded-2xl p-5 sm:p-6 border-2 border-blue-200">
              <h3 className="text-lg sm:text-xl font-bold text-teal-800 mb-4 text-center">
                ⭐ Lo que dicen nuestros clientes
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {[
                  {
                    text: "Después de probar con varios adiestradores sin éxito, encontré en Aula K9 la profesionalidad que necesitaba. Mi perra ya no tira de la correa.",
                    author: "Laura M., Estella",
                    rating: 5
                  },
                  {
                    text: "La evaluación fue muy completa. Me explicaron todo con claridad y las pautas funcionaron desde el primer día. Totalmente recomendable.",
                    author: "Javier P., Pamplona",
                    rating: 5
                  },
                  {
                    text: "Tenía un caso de agresividad con mi perro. El profesionalismo y la paciencia de Don Bandi fueron increíbles. Ahora vivimos tranquilos.",
                    author: "Ana S., Tafalla",
                    rating: 5
                  }
                ].map((testimonial, index) => (
                  <div key={index} className="bg-white rounded-xl p-4 shadow-md hover:scale-105 transition-all duration-300">
                    <div className="flex mb-2">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <i key={i} className="ri-star-fill text-yellow-400 text-sm"></i>
                      ))}
                    </div>
                    <p className="text-gray-700 text-xs italic mb-2">"{testimonial.text}"</p>
                    <p className="text-teal-600 text-xs font-semibold">— {testimonial.author}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Garantía y confianza */}
            <div className="mt-6 bg-gradient-to-r from-green-50 to-teal-50 rounded-2xl p-5 sm:p-6 border-2 border-green-300">
              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <i className="ri-shield-check-line text-green-600 text-3xl"></i>
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-teal-800 mb-2">Garantía de Profesionalidad</h3>
                <p className="text-gray-700 text-sm sm:text-base mb-3 max-w-2xl mx-auto">
                  Trabajamos con <strong>acreditación oficial</strong> y más de <strong>40 años de experiencia</strong>. 
                  No prometemos soluciones mágicas, pero sí un acompañamiento profesional, ético y efectivo.
                </p>
                <div className="flex flex-wrap justify-center gap-2">
                  <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-xs font-semibold">
                    ✓ Acreditación oficial
                  </span>
                  <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-xs font-semibold">
                    ✓ Métodos no violentos
                  </span>
                  <span className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-xs font-semibold">
                    ✓ Bienestar animal
                  </span>
                </div>
              </div>
            </div>

            {/* Importante */}
            <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-r-lg mt-6">
              <div className="flex items-start space-x-2">
                <i className="ri-alert-fill text-red-500 text-lg flex-shrink-0"></i>
                <div>
                  <h4 className="text-red-800 font-bold text-sm mb-1">⚠️ MUY IMPORTANTE</h4>
                  <p className="text-red-700 text-xs sm:text-sm">
                    LOS CASOS DE EVALUACIONES POR AGRESIONES A PERSONAS, ANIMALES o BIENES, REQUIEREN UNA CONSULTA PREVIA PARA UNA EVALUACIÓN ESPECÍFICA
                  </p>
                </div>
              </div>
            </div>

            {/* CTA final de urgencia */}
            <div className="mt-6 bg-gradient-to-r from-orange-500 to-red-500 rounded-2xl p-5 sm:p-6 text-white text-center">
              <h3 className="text-xl sm:text-2xl font-bold mb-2">🔥 No esperes más</h3>
              <p className="text-sm sm:text-base mb-4 max-w-xl mx-auto">
                Cada día que pasa sin ayuda profesional puede empeorar el problema. 
                <strong> Actúa ahora</strong> y mejora la vida de tu perro.
              </p>
              <button
                onClick={() => scrollToSection('contacto')}
                className="bg-white hover:bg-gray-100 hover:scale-105 text-orange-600 px-8 py-3 rounded-full font-bold transition-all duration-300 shadow-lg text-sm sm:text-base transform"
              >
                ✅ Sí, quiero ayuda profesional ahora
              </button>
              <p className="text-xs sm:text-sm mt-3 text-white/90">
                ⏰ Plazas limitadas • Respuesta en 24h • Sin compromiso
              </p>
            </div>
          </div>
        </section>

        {/* Consulta Online */}
        <section id="consulta-online" className="py-8 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-50 via-teal-50 to-white">
          <div className="max-w-4xl mx-auto">
            {/* Badge destacado */}
            <div className="bg-teal-100 border-2 border-teal-300 rounded-xl p-3 mb-6 text-center">
              <p className="text-teal-800 font-bold text-sm sm:text-base flex items-center justify-center">
                <i className="ri-video-chat-line text-teal-600 mr-2 text-lg"></i>
                🖥️ Servicio de atención virtual disponible
              </p>
            </div>

            {/* Título principal */}
            <div className="text-center mb-6">
              <h2 className="text-2xl sm:text-3xl font-bold text-teal-800 mb-2">Consulta Online</h2>
              <div className="inline-flex items-center bg-orange-500 text-white px-4 py-2 rounded-full text-lg sm:text-xl font-bold shadow-lg">
                <i className="ri-time-line mr-2"></i>
                30 min – 20 €
              </div>
            </div>

            {/* Tarjeta principal */}
            <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-xl border-2 border-teal-200 mb-6">
              {/* Descripción */}
              <div className="mb-6">
                <p className="text-gray-700 text-sm sm:text-base leading-relaxed text-center">
                  Sesión virtual personalizada para resolver <strong>problemas de comportamiento</strong>, educación básica, adaptación de cachorros, ansiedad, ladridos o dudas generales.
                </p>
              </div>

              {/* Lo que incluye */}
              <div className="bg-gradient-to-r from-teal-50 to-blue-50 rounded-xl p-5 mb-6 border border-teal-100">
                <h3 className="text-lg font-bold text-teal-800 mb-4 flex items-center">
                  <i className="ri-checkbox-circle-fill text-teal-500 mr-2"></i>
                  ¿Qué incluye?
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {[
                    { icon: 'ri-search-eye-line', title: 'Evaluación individual', desc: 'Análisis detallado de tu caso' },
                    { icon: 'ri-file-list-3-line', title: 'Plan personalizado', desc: 'Pautas de trabajo adaptadas' },
                    { icon: 'ri-vidicon-line', title: 'Videollamada privada', desc: 'Sesión 1 a 1 contigo' }
                  ].map((item, index) => (
                    <div key={index} className="bg-white rounded-lg p-4 shadow-sm text-center hover:scale-105 transition-all duration-300">
                      <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-3">
                        <i className={`${item.icon} text-teal-600 text-xl`}></i>
                      </div>
                      <h4 className="font-bold text-teal-800 text-sm mb-1">{item.title}</h4>
                      <p className="text-gray-600 text-xs">{item.desc}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Detalles */}
              <div className="flex flex-wrap justify-center gap-3 mb-6">
                <span className="bg-blue-100 text-blue-800 px-4 py-2 rounded-full text-sm font-semibold flex items-center">
                  <i className="ri-timer-line mr-2"></i>
                  Duración: máx. 30 min
                </span>
                <span className="bg-orange-100 text-orange-800 px-4 py-2 rounded-full text-sm font-semibold flex items-center">
                  <i className="ri-price-tag-3-line mr-2"></i>
                  Precio: 20 €
                </span>
              </div>
            </div>

            {/* Cómo funciona */}
            <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-xl border-2 border-orange-200 mb-6">
              <h3 className="text-xl font-bold text-teal-800 mb-5 text-center flex items-center justify-center">
                <i className="ri-route-line text-orange-500 mr-2"></i>
                ¿Cómo funciona?
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {[
                  { num: '1', title: 'Realiza el pago', desc: 'Por Bizum o transferencia bancaria', icon: 'ri-bank-card-line', color: 'teal' },
                  { num: '2', title: 'Envía el justificante', desc: 'Por WhatsApp al +34 722131257', icon: 'ri-whatsapp-line', color: 'green' },
                  { num: '3', title: 'Confirmamos y agendamos', desc: 'Te contactamos para fijar la cita', icon: 'ri-calendar-check-line', color: 'orange' }
                ].map((paso, index) => (
                  <div key={index} className="relative">
                    <div className={`bg-gradient-to-br from-${paso.color}-50 to-${paso.color}-100 rounded-xl p-5 text-center hover:scale-105 transition-all duration-300 border border-${paso.color}-200 h-full`}>
                      <div className={`w-10 h-10 bg-${paso.color}-500 rounded-full flex items-center justify-center mx-auto mb-3 shadow-lg`}>
                        <span className="text-white text-lg font-bold">{paso.num}</span>
                      </div>
                      <div className={`w-12 h-12 bg-white rounded-full flex items-center justify-center mx-auto mb-3 shadow-sm`}>
                        <i className={`${paso.icon} text-${paso.color}-600 text-2xl`}></i>
                      </div>
                      <h4 className="font-bold text-gray-800 text-sm mb-1">{paso.title}</h4>
                      <p className="text-gray-600 text-xs">{paso.desc}</p>
                    </div>
                    {index < 2 && (
                      <div className="hidden sm:block absolute top-1/2 -right-2 transform -translate-y-1/2 z-10">
                        <i className="ri-arrow-right-line text-gray-300 text-2xl"></i>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Horario de atención */}
            <div className="bg-gradient-to-r from-blue-100 to-teal-100 rounded-2xl p-5 sm:p-6 mb-6 border-2 border-blue-200">
              <h3 className="text-lg font-bold text-teal-800 mb-4 text-center flex items-center justify-center">
                <i className="ri-calendar-schedule-line text-blue-600 mr-2"></i>
                Horario de atención
              </h3>
              <div className="flex flex-col sm:flex-row justify-center items-center gap-4">
                <div className="bg-white rounded-xl px-6 py-4 shadow-sm text-center">
                  <p className="text-gray-800 font-bold text-sm mb-1">📅 Lunes a Viernes</p>
                </div>
                <div className="flex flex-col sm:flex-row gap-3">
                  <div className="bg-white rounded-xl px-5 py-3 shadow-sm text-center">
                    <p className="text-teal-700 font-semibold text-sm">🕙 10:00 – 14:00</p>
                  </div>
                  <div className="bg-white rounded-xl px-5 py-3 shadow-sm text-center">
                    <p className="text-teal-700 font-semibold text-sm">🕓 16:00 – 20:00</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Botón principal WhatsApp */}
            <div className="text-center">
              <a
                href="https://wa.me/34722131257"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center bg-green-500 hover:bg-green-600 hover:scale-105 text-white px-8 py-4 rounded-full font-bold transition-all duration-300 shadow-xl text-base sm:text-lg transform"
              >
                <i className="ri-whatsapp-line text-2xl mr-3"></i>
                Enviar justificante y agendar por WhatsApp
              </a>
              <p className="text-gray-500 text-xs sm:text-sm mt-3">
                💬 Te responderemos lo antes posible para confirmar tu cita
              </p>
            </div>

            {/* Nota de confianza */}
            <div className="mt-6 bg-white rounded-xl p-4 border border-gray-200 text-center">
              <div className="flex flex-wrap justify-center gap-2">
                <span className="bg-teal-50 text-teal-700 px-3 py-1 rounded-full text-xs font-semibold">
                  ✓ Atención personalizada
                </span>
                <span className="bg-orange-50 text-orange-700 px-3 py-1 rounded-full text-xs font-semibold">
                  ✓ +40 años experiencia
                </span>
                <span className="bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-xs font-semibold">
                  ✓ Profesional acreditado
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* Nuestra Metodología */}
        <section className="py-6 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-4">
              <h2 className="text-xl sm:text-2xl font-bold text-teal-800 mb-2">Nuestra Metodología</h2>
            </div>
            
            <div className="bg-gradient-to-br from-teal-50 to-blue-50 rounded-2xl p-5 sm:p-6 border-2 border-teal-200">
              <p className="text-teal-800 text-base sm:text-lg font-semibold text-center mb-4">
                Con NUESTRA METODOLOGÍA
              </p>
              
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-5">
                {[
                  { icon: 'ri-book-open-line', text: 'Conocimiento' },
                  { icon: 'ri-heart-pulse-line', text: 'Dedicación' },
                  { icon: 'ri-trophy-line', text: 'Esfuerzo' },
                  { icon: 'ri-shield-check-line', text: 'Disciplina' },
                  { icon: 'ri-tools-line', text: 'Trabajo' }
                ].map((item, index) => (
                  <div key={index} className="bg-white rounded-xl p-3 shadow-md text-center hover:bg-orange-50 hover:scale-105 transition-all duration-300 transform">
                    <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-2">
                      <i className={`${item.icon} text-teal-600 text-lg`}></i>
                    </div>
                    <p className="text-gray-700 font-semibold text-xs">{item.text}</p>
                  </div>
                ))}
              </div>

              <div className="text-center space-y-4">
                <p className="text-xl sm:text-2xl font-bold text-teal-800">
                  Tú también puedes
                </p>
                
                <div className="bg-white rounded-xl p-4 sm:p-5 shadow-lg max-w-md mx-auto">
                  <p className="text-2xl sm:text-3xl font-bold text-orange-500 mb-3">
                    ¿Te ayudamos a hacerlo?
                  </p>
                  <button
                    onClick={() => scrollToSection('contacto')}
                    className="w-full bg-orange-500 hover:bg-orange-600 hover:scale-105 text-white px-6 py-3 rounded-full font-semibold transition-all duration-300 shadow-md text-sm sm:text-base transform"
                  >
                    Sí, quiero vuestra ayuda
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Por qué confiar */}
        <section className="py-6 px-4 sm:px-6 lg:px-8 bg-white/70">
          <div className="max-w-4xl mx-auto">
            {/* Imagen fuera del desplegable */}
            <div className="relative rounded-xl overflow-hidden shadow-lg mb-4 hover:scale-105 transition-all duration-300 transform bg-gray-100">
              <img
                src="https://readdy.ai/api/search-image?query=Professional%20certified%20dog%20trainer%20with%20credentials%20and%20certificates%2C%20experienced%20trainer%20showing%20professionalism%2C%20realistic%20photography%20professional%20setting%20natural%20lighting%20high%20quality%20detailed%20image%20the%20main%20subject%20should%20take%20up%2070%25%20of%20the%20frame%20centered%20composition&width=800&height=450&seq=confiar-profesional-img&orientation=landscape"
                alt="Profesionalidad y confianza"
                className="w-full h-56 sm:h-64 lg:h-80 object-contain"
              />
            </div>
            
            <button
              onClick={() => toggleSection('confiar')}
              className="w-full flex items-center justify-between bg-white rounded-xl p-4 shadow-md mb-3 hover:bg-orange-50 hover:scale-105 transition-all duration-300 transform"
            >
              <h2 className="text-base sm:text-xl font-bold text-teal-800">¿Por qué confiar en un profesional Acreditado?</h2>
              <i className={`${expandedSections['confiar'] ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-2xl text-teal-600`}></i>
            </button>
            
            {expandedSections['confiar'] && (
              <div className="space-y-4 animate-fadeIn">
                <div className="bg-white rounded-xl p-4 sm:p-5 shadow-md">
                  <p className="text-gray-700 text-sm sm:text-base leading-relaxed text-center mb-3">
                    La educación y el comportamiento canino <strong>no son una opinión</strong>. Son una disciplina que requiere formación, experiencia y acreditación oficial.
                  </p>
                </div>
                <div className="bg-gradient-to-r from-teal-50 to-blue-50 rounded-xl p-4 border border-teal-100">
                  <p className="text-teal-800 text-sm sm:text-base font-semibold text-center">
                    En <span style={{ fontFamily: 'Georgia, serif', fontWeight: 'bold', fontStyle: 'italic' }}>Aula K</span><span style={{ fontFamily: 'Arial, sans-serif', fontWeight: 'bold', fontStyle: 'italic' }}>9</span><span style={{ fontFamily: 'Georgia, serif', fontWeight: 'bold', fontStyle: 'italic' }}> Arete Can</span> trabajamos desde:
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {[
                      { icon: 'ri-book-open-line', title: 'El conocimiento técnico', desc: 'Formación oficial y actualizada' },
                      { icon: 'ri-time-line', title: 'La experiencia real', desc: 'Más de 40 años de práctica' },
                      { icon: 'ri-shield-check-line', title: 'La ética profesional', desc: 'Compromiso con el bienestar animal' },
                      { icon: 'ri-heart-line', title: 'El respeto', desc: 'Al perro y a su familia' }
                    ].map((valor, index) => (
                      <div key={index} className="bg-white rounded-lg p-3 shadow-sm flex items-center space-x-3 hover:bg-orange-50 hover:scale-105 transition-all duration-300 transform">
                        <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <i className={`${valor.icon} text-teal-600 text-base`}></i>
                        </div>
                        <div>
                          <h4 className="font-bold text-teal-800 text-xs mb-0.5">{valor.title}</h4>
                          <p className="text-gray-600 text-xs">{valor.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Galería de Fotos - Entrenamientos */}
        <section id="galeria" className="py-6 px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-6">
              <h2 className="text-2xl sm:text-3xl font-bold text-teal-800 mb-2">Galería de Entrenamientos</h2>
              <p className="text-gray-600 text-sm sm:text-base">Momentos reales de nuestro trabajo con clientes y sus perros</p>
            </div>
            
            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
              {/* Foto 1 de 8 - NUEVA PRIMERA FOTO */}
              <div className="relative aspect-square rounded-xl overflow-hidden shadow-lg hover:scale-110 hover:z-10 transition-all duration-300 transform cursor-pointer group">
                <img
                  src="https://static.readdy.ai/image/1641cce445e23a33732a9af05560332a/851b1c450b45cf7325be85924c8e03cd.jpeg"
                  alt="Entrenamiento profesional 1"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-teal-600/0 group-hover:bg-teal-600/10 transition-all duration-300"></div>
              </div>

              {/* Foto 2 de 8 */}
              <div className="relative aspect-square rounded-xl overflow-hidden shadow-lg hover:scale-110 hover:z-10 transition-all duration-300 transform cursor-pointer group">
                <img
                  src="https://static.readdy.ai/image/1641cce445e23a33732a9af05560332a/9d55e8003f2735a04c3a6fd39cf5ae26.png"
                  alt="Entrenamiento profesional 2"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-teal-600/0 group-hover:bg-teal-600/10 transition-all duration-300"></div>
              </div>

              {/* Foto 3 de 8 */}
              <div className="relative aspect-square rounded-xl overflow-hidden shadow-lg hover:scale-110 hover:z-10 transition-all duration-300 transform cursor-pointer group">
                <img
                  src="https://static.readdy.ai/image/1641cce445e23a33732a9af05560332a/c0155a7f9d1db95080c5cac16905e41f.jpeg"
                  alt="Entrenamiento profesional 3"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-teal-600/0 group-hover:bg-teal-600/10 transition-all duration-300"></div>
              </div>

              {/* Foto 4 de 8 */}
              <div className="relative aspect-square rounded-xl overflow-hidden shadow-lg hover:scale-110 hover:z-10 transition-all duration-300 transform cursor-pointer group">
                <img
                  src="https://static.readdy.ai/image/1641cce445e23a33732a9af05560332a/526ae413fc50b33fcf8e3c987021a607.jpeg"
                  alt="Entrenamiento profesional 4"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-teal-600/0 group-hover:bg-teal-600/10 transition-all duration-300"></div>
              </div>

              {/* Foto 5 de 8 */}
              <div className="relative aspect-square rounded-xl overflow-hidden shadow-lg hover:scale-110 hover:z-10 transition-all duration-300 transform cursor-pointer group">
                <img
                  src="https://static.readdy.ai/image/1641cce445e23a33732a9af05560332a/8f9c467ffc72e89316b0ef791af1a6c1.jpeg"
                  alt="Entrenamiento profesional 5"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-teal-600/0 group-hover:bg-teal-600/10 transition-all duration-300"></div>
              </div>

              {/* Foto 6 de 8 */}
              <div className="relative aspect-square rounded-xl overflow-hidden shadow-lg hover:scale-110 hover:z-10 transition-all duration-300 transform cursor-pointer group">
                <img
                  src="https://static.readdy.ai/image/1641cce445e23a33732a9af05560332a/aacff7fcdc174d76137d664d72cfbc48.jpeg"
                  alt="Entrenamiento profesional 6"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-teal-600/0 group-hover:bg-teal-600/10 transition-all duration-300"></div>
              </div>

              {/* Foto 7 de 8 */}
              <div className="relative aspect-square rounded-xl overflow-hidden shadow-lg hover:scale-110 hover:z-10 transition-all duration-300 transform cursor-pointer group">
                <img
                  src="https://static.readdy.ai/image/1641cce445e23a33732a9af05560332a/8e9d5f55f5381042f112cd2380d2842d.jpeg"
                  alt="Entrenamiento profesional 7"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-teal-600/0 group-hover:bg-teal-600/10 transition-all duration-300"></div>
              </div>

              {/* Foto 8 de 8 */}
              <div className="relative aspect-square rounded-xl overflow-hidden shadow-lg hover:scale-110 hover:z-10 transition-all duration-300 transform cursor-pointer group">
                <img
                  src="https://static.readdy.ai/image/1641cce445e23a33732a9af05560332a/69b59b97bba827098d22ea8c7d7e4cb7.jpeg"
                  alt="Entrenamiento profesional 8"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-teal-600/0 group-hover:bg-teal-600/10 transition-all duration-300"></div>
              </div>
            </div>
          </div>
        </section>

        {/* Prompt motivacional 5 */}
        <section className="py-6 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-purple-100 to-pink-100">
          <div className="max-w-4xl mx-auto text-center">
            <div className="bg-white rounded-2xl p-5 sm:p-6 shadow-lg border-2 border-purple-300">
              <h3 className="text-lg sm:text-xl font-bold text-purple-600 mb-2">¿Preparado para el cambio?</h3>
              <p className="text-gray-700 text-sm sm:text-base mb-3">Motivación continua hacia el bienestar de tu perro</p>
              <button
                onClick={() => scrollToSection('contacto')}
                className="bg-purple-500 hover:bg-purple-600 hover:scale-105 text-white px-6 py-2 rounded-full font-semibold transition-all duration-300 text-sm shadow-md transform"
              >
                ¡Empezamos hoy!
              </button>
            </div>
          </div>
        </section>

        {/* Quién está detrás - Desplegable */}
        <section id="profesional" className="py-6 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <button
              onClick={() => toggleSection('profesional')}
              className="w-full flex items-center justify-between bg-white rounded-xl p-4 shadow-md mb-3 hover:bg-orange-50 hover:scale-105 transition-all duration-300 transform"
            >
              <h2 className="text-base sm:text-xl font-bold text-teal-800">¿Quién está detrás de todo esto?</h2>
              <i className={`${expandedSections['profesional'] ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-2xl text-teal-600`}></i>
            </button>
            
            {expandedSections['profesional'] && (
              <div className="space-y-4 animate-fadeIn">
                <div className="relative rounded-xl overflow-hidden shadow-xl bg-gray-100">
                  <img
                    src="https://static.readdy.ai/image/1641cce445e23a33732a9af05560332a/4757fbb360c60084b87d435a8ee7710c.jpeg"
                    alt="Instructor profesional"
                    className="w-full h-72 sm:h-80 lg:h-96 object-contain"
                  />
                </div>
                <div className="bg-white rounded-xl p-4 sm:p-5 shadow-md">
                  <p className="text-gray-700 text-sm sm:text-base leading-relaxed text-center mb-3">
                    Contamos con un <strong>Técnico Instructor Formador de Formadores</strong> con más de 40 años de experiencia profesional, en formación continua y con acreditaciones oficiales.
                  </p>
                </div>
                <div className="bg-teal-50 rounded-xl p-4 sm:p-5">
                  <h3 className="text-base sm:text-lg font-bold text-teal-800 mb-3">Acreditaciones principales:</h3>
                  <ul className="space-y-2">
                    {[
                      'Técnico Evaluador de futuros profesionales del adiestramiento canino.',
                      'Adiestrador Canino Profesional (Real Decreto 659/2023).',
                      'Personal Adiestrador Canino Profesional por el Gobierno Foral de Navarra.',
                      'Técnico en comportamiento y conductas caninas.',
                      'Técnico Instructor Formador de Guías Caninos de Seguridad Privada.',
                      'Adiestrador de Perros de Defensa y Vigilancia.',
                      'Juez y Docente del Programa Ciudadano Canino Ejemplar (ANACP).'
                    ].map((acreditacion, index) => (
                      <li key={index} className="text-gray-700 text-xs sm:text-sm leading-relaxed pl-3 border-l-2 border-teal-300">
                        {acreditacion}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="bg-orange-50 border-l-4 border-orange-500 p-4 rounded-r-lg">
                  <p className="text-orange-800 font-semibold text-sm sm:text-base">
                    👉 No todo el mundo puede ejercer profesionalmente. Nosotros sí.
                  </p>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Compromiso y Valores */}
        <section className="py-6 px-4 sm:px-6 lg:px-8 bg-white/70">
          <div className="max-w-4xl mx-auto">
            {/* Imagen fuera del desplegable */}
            <div className="relative rounded-xl overflow-hidden shadow-lg mb-4 hover:scale-105 transition-all duration-300 transform bg-gray-100">
              <img
                src="https://readdy.ai/api/search-image?query=Professional%20dog%20training%20values%20and%20commitment%2C%20ethical%20dog%20training%20methods%2C%20positive%20reinforcement%20with%20happy%20dogs%2C%20realistic%20photography%20outdoor%20setting%20natural%20lighting%20high%20quality%20detailed%20image%20the%20main%20subject%20should%20take%20up%2070%25%20of%20the%20frame%20centered%20composition&width=800&height=450&seq=valores-compromiso-img&orientation=landscape"
                alt="Valores y compromiso profesional"
                className="w-full h-56 sm:h-64 lg:h-80 object-contain"
              />
            </div>
            
            <button
              onClick={() => toggleSection('valores')}
              className="w-full flex items-center justify-between bg-white rounded-xl p-4 shadow-md mb-3 hover:bg-orange-50 hover:scale-105 transition-all duration-300 transform"
            >
              <h2 className="text-xl sm:text-2xl font-bold text-teal-800">Compromiso y Valores</h2>
              <i className={`${expandedSections['valores'] ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-2xl text-teal-600`}></i>
            </button>
            
            {expandedSections['valores'] && (
              <div className="space-y-3 animate-fadeIn">
                <p className="text-center text-sm sm:text-base text-gray-600 mb-3">Nuestro trabajo se basa en:</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {[
                    { icon: 'ri-heart-pulse-line', text: 'Bienestar canino por encima de todo' },
                    { icon: 'ri-shield-check-line', text: 'Intervenciones profesionales, responsables y éticas' },
                    { icon: 'ri-graduation-cap-line', text: 'Formación continua y actualizada' },
                    { icon: 'ri-hand-heart-line', text: 'Acompañamiento real a los propietarios' }
                  ].map((valor, index) => (
                    <div key={index} className="bg-white rounded-xl p-4 shadow-md border border-teal-100 hover:bg-orange-50 hover:scale-105 transition-all duration-300 transform">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <i className={`${valor.icon} text-teal-600 text-lg`}></i>
                        </div>
                        <p className="text-gray-700 text-sm font-medium">{valor.text}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="bg-gradient-to-r from-orange-500 to-red-500 rounded-xl p-4 text-white text-center">
                  <p className="text-sm font-bold mb-2">Nuestro lema de trabajo:</p>
                  <p className="text-lg sm:text-xl font-bold mb-2">
                    Planificación, paciencia, perseverancia y trabajo
                  </p>
                  <p className="text-xs sm:text-sm font-semibold">
                    Siempre bajo el paraguas del respeto animal
                  </p>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Cultura de Empresa - Desplegable */}
        <section className="py-6 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <button
              onClick={() => toggleSection('cultura')}
              className="w-full flex items-center justify-between bg-white rounded-xl p-4 shadow-md mb-3 hover:bg-orange-50 hover:scale-105 transition-all duration-300 transform"
            >
              <h2 className="text-xl sm:text-2xl font-bold text-teal-800">Cultura de Empresa</h2>
              <i className={`${expandedSections['cultura'] ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-2xl text-teal-600`}></i>
            </button>
            
            {expandedSections['cultura'] && (
              <div className="space-y-4 animate-fadeIn">
                <p className="text-center text-base sm:text-lg text-orange-500 font-semibold">Nuestros tres pilares</p>
                
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                  {/* Misión */}
                  <div className="bg-white rounded-xl p-4 shadow-md border-l-4 border-teal-500 hover:bg-orange-50 hover:scale-105 transition-all duration-300 transform">
                    <div className="flex items-center space-x-2 mb-2">
                      <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center">
                        <span className="text-teal-600 text-base font-bold">1</span>
                      </div>
                      <h4 className="text-lg font-bold text-teal-800">Misión</h4>
                    </div>
                    <p className="text-gray-700 text-xs sm:text-sm leading-relaxed">
                      Ayudar a los propietarios a satisfacer las necesidades de sus perros a través de la formación, la información y el asesoramiento. Fomentar una convivencia armoniosa mediante educación canina profesional.
                    </p>
                  </div>

                  {/* Visión */}
                  <div className="bg-white rounded-xl p-4 shadow-md border-l-4 border-blue-500 hover:bg-orange-50 hover:scale-105 transition-all duration-300 transform">
                    <div className="flex items-center space-x-2 mb-2">
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                        <span className="text-blue-600 text-base font-bold">2</span>
                      </div>
                      <h4 className="text-lg font-bold text-teal-800">Visión</h4>
                    </div>
                    <p className="text-gray-700 text-xs sm:text-sm leading-relaxed">
                      Ser un referente en la educación y el adiestramiento sin violencia. Lograr que los perros sean mejor recibidos en la sociedad por ser animales educados, funcionales y equilibrados.
                    </p>
                  </div>

                  {/* Valores */}
                  <div className="bg-white rounded-xl p-4 shadow-md border-l-4 border-orange-500 hover:bg-orange-50 hover:scale-105 transition-all duration-300 transform lg:col-span-1">
                    <div className="flex items-center space-x-2 mb-3">
                      <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
                        <span className="text-orange-600 text-base font-bold">3</span>
                      </div>
                      <h4 className="text-lg font-bold text-teal-800">Valores</h4>
                    </div>
                    <div className="space-y-2">
                      {[
                        { title: 'Bienestar Animal', icon: 'ri-heart-pulse-line' },
                        { title: 'Adiestramiento No Violento', icon: 'ri-shield-heart-line' },
                        { title: 'Pasión y Vocación', icon: 'ri-heart-line' },
                        { title: 'Profesionalidad', icon: 'ri-graduation-cap-line' },
                        { title: 'El Gran Apego/Vínculo', icon: 'ri-links-line' },
                        { title: 'Accesibilidad', icon: 'ri-hand-coin-line' }
                      ].map((valor, index) => (
                        <div key={index} className="flex items-center space-x-2">
                          <i className={`${valor.icon} text-teal-600 text-sm`}></i>
                          <span className="text-gray-700 text-xs">{valor.title}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Lema */}
                <div className="bg-gradient-to-r from-orange-500 to-red-500 rounded-xl p-5 text-white text-center">
                  <p className="text-sm font-bold mb-2">Nuestro lema de trabajo:</p>
                  <p className="text-lg sm:text-xl font-bold mb-2">
                    Planificación, paciencia, perseverancia y trabajo
                  </p>
                  <p className="text-xs sm:text-sm font-semibold">
                    Siempre bajo el paraguas del respeto animal
                  </p>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Cuéntanos tu caso */}
        <section className="py-6 px-4 sm:px-6 lg:px-8 bg-white/70">
          <div className="max-w-4xl mx-auto">
            <div className="bg-gradient-to-br from-teal-50 to-blue-50 rounded-2xl p-5 sm:p-6 border-2 border-teal-200">
              <h2 className="text-xl sm:text-2xl font-bold text-teal-800 mb-3 text-center">Cuéntanos tu caso</h2>
              <p className="text-gray-700 text-sm sm:text-base mb-4 text-center">Si necesitas ayuda con tu perro:</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-5">
                {[
                  { icon: 'ri-shield-check-line', text: 'No juzgamos' },
                  { icon: 'ri-lightbulb-line', text: 'No improvisamos' },
                  { icon: 'ri-search-eye-line', text: 'Analizamos y orientamos con criterio profesional' },
                  { icon: 'ri-lock-line', text: 'La información se trata de forma confidencial' }
                ].map((item, index) => (
                  <div key={index} className="bg-white rounded-lg p-3 shadow-sm flex items-center space-x-2 hover:bg-orange-50 hover:scale-105 transition-all duration-300 transform">
                    <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <i className={`${item.icon} text-teal-600 text-sm`}></i>
                    </div>
                    <p className="text-gray-700 text-xs sm:text-sm font-medium">{item.text}</p>
                  </div>
                ))}
              </div>
              <p className="text-teal-700 text-sm sm:text-base mb-4 text-center font-medium">El primer paso es entender bien el problema.</p>
              <div className="flex flex-col sm:flex-row gap-2 max-w-lg mx-auto">
                <button
                  onClick={() => scrollToSection('contacto')}
                  className="flex-1 bg-orange-500 hover:bg-orange-600 hover:scale-105 text-white px-6 py-3 rounded-full font-semibold transition-all duration-300 shadow-md text-sm transform"
                >
                  👉 Cuéntanos tu caso
                </button>
                <button
                  onClick={() => scrollToSection('servicios')}
                  className="flex-1 bg-teal-600 hover:bg-teal-700 hover:scale-105 text-white px-6 py-3 rounded-full font-semibold transition-all duration-300 shadow-md text-sm transform"
                >
                  👉 Solicita una evaluación
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Filosofía Final */}
        <section className="py-6 px-4 sm:px-6 lg:px-8 bg-white/70">
          <div className="max-w-4xl mx-auto">
            <div className="space-y-4">
              <div className="bg-white rounded-xl p-5 sm:p-6 shadow-md text-center space-y-3">
                <p className="text-gray-700 text-sm sm:text-base">No trabajamos con recetas rápidas.</p>
                <p className="text-gray-700 text-sm sm:text-base">No prometemos soluciones mágicas.</p>
                <p className="text-teal-800 text-base sm:text-lg font-semibold">
                  Trabajamos con profesionalidad, criterio, experiencia y responsabilidad.
                </p>
              </div>
              
              <div className="bg-gradient-to-r from-teal-600 to-blue-600 rounded-2xl p-5 sm:p-6 text-white text-center">
                <p className="text-xl sm:text-2xl font-bold mb-2">
                  <span style={{ fontFamily: 'Georgia, serif', fontWeight: 'bold', fontStyle: 'italic' }}>Aula K</span><span style={{ fontFamily: 'Arial, sans-serif', fontWeight: 'bold', fontStyle: 'italic' }}>9</span><span style={{ fontFamily: 'Georgia, serif', fontWeight: 'bold', fontStyle: 'italic' }}> Arete Can</span>
                </p>
                <p className="text-sm sm:text-base mb-2">no es una web genérica.</p>
                <p className="text-base sm:text-lg font-semibold">
                  Es un punto de referencia profesional para cuando un perro tiene un problema serio.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Contacto Section */}
        <section id="contacto" className="py-8 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-2xl sm:text-3xl font-bold text-teal-800 mb-2">Contacta con nosotros</h2>
              <p className="text-gray-600 text-sm sm:text-base">Estamos aquí para ayudarte con cualquier consulta sobre tu perro</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* 1. Envíanos un mensaje - Formulario */}
              <div className="bg-gradient-to-br from-teal-50 to-blue-50 rounded-2xl p-6 border border-teal-200">
                <div className="flex items-center space-x-2 mb-4">
                  <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center">
                    <span className="text-teal-600 text-base font-bold">1</span>
                  </div>
                  <h3 className="text-xl font-bold text-teal-800">Envíanos un mensaje</h3>
                </div>
                
                <form 
                  id="contacto-form"
                  data-readdy-form
                  action="https://readdy.ai/api/form/d5pgrtlb27nnv15of0t0"
                  method="POST"
                  className="space-y-4"
                  onSubmit={(e) => {
                    e.preventDefault();
                    const form = e.target as HTMLFormElement;
                    const formData = new FormData(form);
                    const data = new URLSearchParams();
                    
                    for (const [key, value] of formData.entries()) {
                      data.append(key, value as string);
                    }
                    
                    fetch('https://readdy.ai/api/form/d5pgrtlb27nnv15of0t0', {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/x-www-form-urlencoded'
                      },
                      body: data
                    }).then(() => {
                      const successMsg = document.getElementById('success-message');
                      if (successMsg) {
                        successMsg.classList.remove('hidden');
                        form.reset();
                        setTimeout(() => {
                          successMsg.classList.add('hidden');
                        }, 5000);
                      }
                    }).catch(() => {
                      const errorMsg = document.getElementById('error-message');
                      if (errorMsg) {
                        errorMsg.classList.remove('hidden');
                        setTimeout(() => {
                          errorMsg.classList.add('hidden');
                        }, 5000);
                      }
                    });
                  }}
                >
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">
                      Nombre *
                    </label>
                    <input
                      type="text"
                      name="nombre"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm hover:border-orange-300 transition-colors"
                      placeholder="Tu nombre completo"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">
                      Email *
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm hover:border-orange-300 transition-colors"
                      placeholder="tu.email@ejemplo.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">
                      Teléfono
                    </label>
                    <input
                      type="tel"
                      name="telefono"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm hover:border-orange-300 transition-colors"
                      placeholder="Tu número de teléfono"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">
                      Nombre de tu perro y edad
                    </label>
                    <input
                      type="text"
                      name="nombre_perro"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm hover:border-orange-300 transition-colors"
                      placeholder="¿Cómo se llama tu perro?"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">
                      Tipo de consulta *
                    </label>
                    <div className="relative">
                      <select
                        name="tipo_consulta"
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm appearance-none bg-white hover:border-orange-300 transition-colors"
                      >
                        <option value="">Selecciona el tipo de consulta</option>
                        <option value="evaluacion_plan_pautas">Evaluación y Plan de Pautas</option>
                        <option value="evaluacion_pautas_seguimiento">Evaluación, Pautas y Seguimiento</option>
                        <option value="problemas_comportamiento">Problemas de Comportamiento</option>
                        <option value="agresividad">Casos de Agresividad</option>
                        <option value="informacion_general">Información General</option>
                        <option value="otros">Otros</option>
                      </select>
                      <div className="absolute inset-y-0 right-0 flex items-center px-3 pointer-events-none">
                        <i className="ri-arrow-down-s-line text-gray-400"></i>
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">
                      Describe el problema o consulta *
                    </label>
                    <textarea
                      name="mensaje"
                      required
                      maxLength={500}
                      rows={4}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm resize-none hover:border-orange-300 transition-colors"
                      placeholder="Por favor, descríbenos detalladamente el problema de comportamiento de tu perro o la consulta que tienes. Máximo 500 caracteres."
                    ></textarea>
                    <div className="text-right mt-1">
                      <span className="text-xs text-gray-500">Máximo 500 caracteres</span>
                    </div>
                  </div>

                  <div className="flex items-start space-x-2">
                    <input
                      type="checkbox"
                      id="privacy"
                      name="acepta_privacidad"
                      required
                      className="mt-1 h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded"
                    />
                    <label htmlFor="privacy" className="text-xs text-gray-600 leading-relaxed">
                      He leído y acepto la política de privacidad. Entiendo que la información será tratada de forma confidencial y profesional. *
                    </label>
                  </div>

                  <button
                    type="submit"
                    className="w-full bg-orange-500 hover:bg-orange-600 hover:scale-105 text-white px-5 py-3 rounded-full font-semibold transition-all duration-300 shadow-lg text-sm sm:text-base transform"
                  >
                    <i className="ri-send-plane-line mr-2"></i>
                    Enviar mensaje
                  </button>
                </form>

                {/* Mensajes de estado */}
                <div id="success-message" className="hidden mt-4 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg">
                  <div className="flex items-center">
                    <i className="ri-check-circle-line text-green-600 text-lg mr-2"></i>
                    <span className="text-sm">¡Mensaje enviado correctamente! Te responderemos lo antes posible.</span>
                  </div>
                </div>
                
                <div id="error-message" className="hidden mt-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
                  <div className="flex items-center">
                    <i className="ri-error-warning-line text-red-600 text-lg mr-2"></i>
                    <span className="text-sm">Ha ocurrido un error al enviar el mensaje. Por favor, inténtalo de nuevo.</span>
                  </div>
                </div>
              </div>

              {/* Columna derecha: Info + FAQ */}
              <div className="space-y-6">
                {/* 2. Información del contacto */}
                <div className="bg-gradient-to-br from-blue-50 to-teal-50 rounded-2xl p-6 border border-blue-200">
                  <div className="flex items-center space-x-2 mb-4">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                      <span className="text-blue-600 text-base font-bold">2</span>
                    </div>
                    <h3 className="text-xl font-bold text-teal-800">Información del contacto</h3>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="bg-white rounded-xl p-4 shadow-sm hover:bg-orange-50 hover:scale-105 transition-all duration-300 transform">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                          <i className="ri-mail-line text-orange-600 text-lg"></i>
                        </div>
                        <div>
                          <h4 className="font-bold text-gray-800 text-sm">Email</h4>
                          <a 
                            href="mailto:aretecan@gmail.com"
                            className="text-orange-500 hover:text-orange-600 text-sm font-medium transition-colors"
                          >
                            aretecan@gmail.com
                          </a>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white rounded-xl p-4 shadow-sm hover:bg-orange-50 hover:scale-105 transition-all duration-300 transform">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center">
                          <i className="ri-map-pin-line text-teal-600 text-lg"></i>
                        </div>
                        <div>
                          <h4 className="font-bold text-gray-800 text-sm">Ubicación</h4>
                          <p className="text-gray-600 text-sm">
                            Navarra, España
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white rounded-xl p-4 shadow-sm hover:bg-orange-50 hover:scale-105 transition-all duration-300 transform">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                          <i className="ri-time-line text-blue-600 text-lg"></i>
                        </div>
                        <div>
                          <h4 className="font-bold text-gray-800 text-sm">Tiempo de respuesta</h4>
                          <p className="text-gray-600 text-sm">
                            Respondemos en un plazo máximo de 24-48 horas
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Redes sociales */}
                <div className="text-center bg-white rounded-2xl p-4 shadow-md">
                  <h4 className="text-lg font-bold text-teal-800 mb-4">Síguenos en redes sociales</h4>
                  <div className="flex justify-center space-x-4">
                    <a 
                      href="https://www.facebook.com/Aulak9AreteCan" 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="w-12 h-12 bg-blue-600 hover:bg-blue-700 hover:scale-110 rounded-full flex items-center justify-center text-white transition-all duration-300 shadow-lg transform"
                    >
                      <i className="ri-facebook-fill text-xl"></i>
                    </a>
                    <a 
                      href="https://twitter.com/aulak9yaretecan" 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="w-12 h-12 bg-blue-400 hover:bg-blue-500 hover:scale-110 rounded-full flex items-center justify-center text-white transition-all duration-300 shadow-lg transform"
                    >
                      <i className="ri-twitter-fill text-xl"></i>
                    </a>
                    <a 
                      href="https://instagram.com/asociacionaulak9aretecan" 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 hover:scale-110 rounded-full flex items-center justify-center text-white transition-all duration-300 shadow-lg transform"
                    >
                      <i className="ri-instagram-fill text-xl"></i>
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* 3. Preguntas frecuentes - Full width */}
            <div className="mt-8 bg-gradient-to-br from-orange-50 to-red-50 rounded-2xl p-6 border border-orange-200">
              <div className="flex items-center space-x-2 mb-6">
                <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
                  <span className="text-orange-600 text-base font-bold">3</span>
                </div>
                <h3 className="text-xl font-bold text-teal-800">Preguntas frecuentes</h3>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  {
                    question: "¿Cuánto tiempo lleva resolver un problema de comportamiento?",
                    answer: "Cada caso es único. Depende del tipo de problema, la edad del perro, el entorno y la dedicación del propietario. En la evaluación inicial te daremos una estimación realista."
                  },
                  {
                    question: "¿Trabajáis con todos los tipos de perros?",
                    answer: "Sí, trabajamos con perros de todas las razas, edades y tamaños. Nuestro enfoque se adapta a las características individuales de cada perro y su familia."
                  },
                  {
                    question: "¿Qué diferencia hay entre las dos opciones de evaluación?",
                    answer: "La Opción 1 incluye evaluación y pautas para trabajo autónomo. La Opción 2 añade seguimiento semanal y visita adicional si es necesaria. Ideal para casos más complejos."
                  },
                  {
                    question: "¿Atendéis casos de agresividad?",
                    answer: "Sí, pero requieren consulta previa para valoración específica. La seguridad es prioritaria y cada caso de agresividad necesita un análisis profesional detallado."
                  },
                  {
                    question: "¿Ofrecéis garantías en vuestro trabajo?",
                    answer: "No prometemos soluciones mágicas. Trabajamos con profesionalidad y experiencia, pero el éxito depende también del compromiso y constancia del propietario."
                  },
                  {
                    question: "¿Realizáis el trabajo a domicilio?",
                    answer: "Sí, las evaluaciones se realizan preferentemente en el entorno habitual del perro para obtener una visión completa del problema y la convivencia familiar."
                  }
                ].map((faq, index) => (
                  <div key={index} className="bg-white rounded-xl shadow-sm border border-orange-200 hover:bg-orange-50 transition-all duration-300">
                    <button
                      onClick={() => toggleSection(`faq-${index}`)}
                      className="w-full p-4 text-left flex items-center justify-between hover:bg-orange-50 transition-colors rounded-xl"
                    >
                      <span className="font-semibold text-gray-800 text-sm pr-4">{faq.question}</span>
                      <i className={`${expandedSections[`faq-${index}`] ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-xl text-orange-500 flex-shrink-0`}></i>
                    </button>
                    {expandedSections[`faq-${index}`] && (
                      <div className="px-4 pb-4">
                        <p className="text-gray-600 text-sm leading-relaxed">{faq.answer}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <div className="mt-6 bg-white rounded-xl p-4 text-center border-2 border-orange-200">
                <p className="text-gray-700 text-sm mb-3">
                  ¿No encuentras respuesta a tu pregunta?
                </p>
                <button
                  onClick={() => {
                    const form = document.getElementById('contacto-form');
                    if (form) {
                      form.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }
                  }}
                  className="bg-orange-500 hover:bg-orange-600 hover:scale-105 text-white px-6 py-2 rounded-full font-semibold transition-all duration-300 text-sm shadow-md transform"
                >
                  Envíanos tu consulta
                </button>
              </div>
            </div>

            {/* Entidades colaboradoras */}
            <div className="mt-8 pt-6 border-t border-gray-200">
              <h3 className="text-lg font-bold text-teal-800 mb-4 text-center">Entidades, organismos y personas. Colaboradoras</h3>
              <div className="bg-gradient-to-r from-teal-50 to-blue-50 rounded-xl p-4 border border-teal-200">
                <p className="text-gray-600 text-sm mb-3 text-center">
                  Próximamente añadiremos información sobre nuestras colaboradoras
                </p>
                <div className="space-y-2">
                  <p className="text-gray-500 text-xs italic text-center">
                    Esta sección se actualizará con los nombres de las colaboradoras
                  </p>
                </div>
              </div>
            </div>

            {/* Copyright */}
            <div className="mt-8 pt-6 border-t border-gray-200 text-center">
              <p className="text-xs text-gray-500">
                Copyright © 2021 Asociación <span style={{ fontFamily: 'Georgia, serif', fontWeight: 'bold', fontStyle: 'italic' }}>Aula K</span><span style={{ fontFamily: 'Arial, sans-serif', fontWeight: 'bold', fontStyle: 'italic' }}>9</span><span style={{ fontFamily: 'Georgia, serif', fontWeight: 'bold', fontStyle: 'italic' }}> Arete Can</span>
              </p>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
